/**
 * Settings: one rail of sections, one form per section. Appearance is per
 * device; Refurbishment, Home buyer and Investor are one server-saved draft
 * with a single Save; Billing lives on its own pages. Which assumption
 * sections appear comes from the registry (`spec.settings`) and the server's
 * component grants (see SettingsRail).
 */
import { useEffect, useMemo, useState } from 'react'
import { Link, useNavigate, useSearch } from '@tanstack/react-router'
import { IconDeviceFloppy } from '@tabler/icons-react'
import { Shell } from '../../app/Shell'
import type { DealModelValueDescriptor } from '../../api/dealModelTypes'
import { SKINS, THEMES, useTheme, type PhotoMode } from '../../theme/ThemeProvider'
import { Alert, Button, Segmented, Skeletons } from '../../ui/kit'
import { BillingSettingsPage } from '../billing/BillingPages'
import { refurbishmentDraftSummary, type RefurbishmentDraft } from './refurbishment'
import { SettingsField, SettingsGroup } from './SettingsFields'
import { SECTIONS, SettingsRail, useSettingsAccess, type AssumptionSection, type SettingsSection } from './SettingsRail'
import { groupSettingsDescriptors, settingsFieldPresentation, settingsSummaryValue, type SettingsValue } from './settingsFieldPresentation'
import { useSettingsDraft, type SettingsDraft } from './useSettingsDraft'

export type { SettingsSection } from './SettingsRail'

export function SettingsPage() {
  const search = useSearch({ from: '/settings' })
  const navigate = useNavigate({ from: '/settings' })
  const access = useSettingsAccess()
  const requested: SettingsSection = search.section ?? 'appearance'
  // A section this account cannot use falls back to the active experience's assumptions (as before).
  const section: SettingsSection = !access.ready || access.visible.includes(requested) ? requested
    : requested === 'refurbishment' || requested === 'homeBuyer' || requested === 'investor' ? (access.preferred ?? 'appearance') : 'appearance'
  const draft = useSettingsDraft(access.canReadDefaults)
  const setSection = (next: SettingsSection) => { if (!draft.busy) void navigate({ to: '/settings', search: next === 'appearance' ? {} : { section: next } }) }
  const current = SECTIONS.find(s => s.id === section) ?? SECTIONS[0]!
  const defaultsSection = section === 'refurbishment' || section === 'homeBuyer' || section === 'investor'

  // An account without product access reviews plans and billing only.
  if (!access.allowsProduct && section !== 'appearance') return <BillingSettingsPage />

  return (
    <Shell
      railLabel="Settings sections"
      rail={<SettingsRail current={section} locked={draft.busy} onSelect={setSection} />}
      head={
        <div className="toolbar">
          <div className="tt"><h1 className="t-page">{current.label}</h1><span className="scope">{section === 'appearance' ? 'Per device' : current.note}</span></div>
          {defaultsSection && access.canReadDefaults ? (
            <div className="ta is-end">
              <span className="t-muted" role="status">{draft.busy ? 'Saving…' : draft.hasChanges ? 'Unsaved changes' : ''}</span>
              <Button kind="primary" busy={draft.busy} disabled={!draft.canSave} onClick={() => { void draft.save() }}>{draft.busy ? null : <IconDeviceFloppy className="ic" aria-hidden="true" />}Save settings</Button>
            </div>
          ) : null}
        </div>
      }
      status={<span>{section === 'appearance' ? 'Appearance is remembered on this device; it is also one click away in the header.' : current.note}</span>}
    >
      {section === 'appearance' ? <AppearanceSection />
        : section === 'billing' ? (
          <div className="page"><Alert info title="Plans & billing" action={<Link to="/settings/billing" className="btn">Open plans &amp; billing</Link>}>Your plan, trial and invoices are on their own page.</Alert></div>
        ) : <DefaultsSection section={section} draft={draft} accessReady={access.ready} accessError={access.error} canReadDefaults={access.canReadDefaults} />}
    </Shell>
  )
}

function AppearanceSection() {
  const { theme, skin, photo, setTheme, setSkin, setPhoto } = useTheme()
  return (
    <div className="settings">
      <div>
        <div className="t-section">Theme</div>
        {THEMES.map(t => <button type="button" key={t.id} className={['opt', theme === t.id ? 'is-on' : ''].join(' ')} onClick={() => setTheme(t.id)} aria-pressed={theme === t.id}><b>{t.label}</b><span>{t.note}</span></button>)}
        <div className="t-section">Photographs on cards</div>
        <Segmented<PhotoMode> wide label="Photographs" value={photo} onChange={setPhoto} options={[{ value: 'cover', label: 'Fill the box' }, { value: 'whole', label: 'Show the whole photo' }]} />
        <p className="t-muted">Only the register cards zoom to fill; the lot page always shows photographs at their own proportions.</p>
      </div>
      <div>
        <div className="t-section">Skin</div>
        {SKINS.map(s => <button type="button" key={s.id} className={['skcard', skin === s.id ? 'is-on' : ''].join(' ')} onClick={() => setSkin(s.id)} aria-pressed={skin === s.id}><i className="prev skin-swatch" style={{ '--sw': s.swatch, '--ac': s.accent } as React.CSSProperties}><i /></i><div><b>{s.label}</b><span>{s.note}</span></div></button>)}
        <p className="t-muted">A skin changes tokens only. Layouts, rows and cards are the same in every skin, so a fourth skin is one block in tokens.css.</p>
      </div>
    </div>
  )
}

/* ---------- refurbishment / home buyer / investor ---------- */

// Presentation of the preference contract (not model inputs).
const METHOD: DealModelValueDescriptor = { key: 'refurbishmentMode', semanticType: 'enum', nullable: false, enumValues: ['none', 'auto', 'fixed', 'rate'] }
const PROFILE: DealModelValueDescriptor = { key: 'refurbishmentProfile', semanticType: 'enum', nullable: false, enumValues: ['light', 'standard', 'full'] }
const AMOUNT: DealModelValueDescriptor = { key: 'refurbishmentAmountPence', semanticType: 'moneyPence', nullable: false, enumValues: [] }
const RATE: DealModelValueDescriptor = { key: 'refurbishmentRatePence', semanticType: 'moneyPence', nullable: false, enumValues: [] }
const AREA_UNIT: DealModelValueDescriptor = { key: 'refurbishmentAreaUnit', semanticType: 'enum', nullable: false, enumValues: ['sqm', 'sqft'] }
const METHOD_LABELS = { none: 'None', auto: 'Auto estimate', fixed: 'Fixed amount', rate: 'Per sq ft / m²' }
const AREA_UNIT_LABELS = { sqm: 'Per m²', sqft: 'Per sq ft' }

function RefurbishmentEditor({ draft, onChange }: { draft: RefurbishmentDraft; onChange: (draft: RefurbishmentDraft) => void }) {
  const changeField = (key: string, value: SettingsValue) => {
    if (key === METHOD.key && (value === 'none' || value === 'auto' || value === 'fixed' || value === 'rate')) onChange({ ...draft, mode: value })
    else if (key === PROFILE.key && (value === 'light' || value === 'standard' || value === 'full')) onChange({ ...draft, profile: value })
    else if (key === AREA_UNIT.key && (value === 'sqm' || value === 'sqft')) onChange({ ...draft, areaUnit: value })
    else if (key === AMOUNT.key && (typeof value === 'string' || value === null)) onChange({ ...draft, amountPence: value })
    else if (key === RATE.key && (typeof value === 'string' || value === null)) onChange({ ...draft, ratePence: value })
  }
  return (
    <>
      <SettingsGroup title="Default budget" description="Budget amounts include VAT and contingency. Auto uses the stored estimate for your selected scope.">
        <SettingsField descriptor={METHOD} value={draft.mode} onChange={changeField} optionLabels={METHOD_LABELS} presentation={{ label: 'Budget method', kind: 'enum' }} />
        {draft.mode === 'auto' ? <SettingsField descriptor={PROFILE} value={draft.profile} onChange={changeField} presentation={{ label: 'Estimated works scope', kind: 'enum', help: 'Uses the stored Light, Standard or Full estimate. Missing estimates remain unknown.' }} /> : null}
        {draft.mode === 'fixed' ? <SettingsField descriptor={AMOUNT} value={draft.amountPence} onChange={changeField} presentation={{ label: 'All-in budget', kind: 'pence', help: 'Your total refurbishment allowance, including VAT and contingency.' }} /> : null}
        {draft.mode === 'rate' ? <>
          <SettingsField descriptor={RATE} value={draft.ratePence} onChange={changeField} presentation={{ label: 'Refurbishment rate', kind: 'pence', help: 'An all-in rate including VAT and contingency, multiplied by the property’s floor area. A missing floor area leaves the budget unknown.' }} />
          <SettingsField descriptor={AREA_UNIT} value={draft.areaUnit} onChange={changeField} optionLabels={AREA_UNIT_LABELS} presentation={{ label: 'Rate area unit', kind: 'enum' }} />
        </> : null}
      </SettingsGroup>
      <p className="set-note">Used for Home buyer and Investor. A property’s own budget takes priority.</p>
      {draft.mode === 'rate' ? <p className="set-note">A missing floor area leaves the budget unknown.</p> : null}
    </>
  )
}

function DefaultsSection({ section, draft, accessReady, accessError, canReadDefaults }: { section: Exclude<SettingsSection, 'appearance' | 'billing'>; draft: SettingsDraft; accessReady: boolean; accessError: string | null; canReadDefaults: boolean }) {
  const { settings, preferences, saveSettings, savePreferences } = draft
  const [selectedGroups, setSelectedGroups] = useState<Partial<Record<AssumptionSection, string>>>({})
  const grouped: AssumptionSection | null = section === 'homeBuyer' || section === 'investor' ? section : null
  const fundingMethod = draft.assumptions.homeBuyerFundingMethod
  const groups = useMemo(() => {
    if (!settings.data || !grouped) return []
    const descriptors = settings.data.defaultAssumptionDescriptors.filter(descriptor =>
      !['homeBuyerRefurbMode', 'homeBuyerRefurbBudget'].includes(descriptor.key)
      && descriptor.key.startsWith('homeBuyer') === (grouped === 'homeBuyer'))
    // Cash buyers have no mortgage illustration.
    return groupSettingsDescriptors(descriptors, grouped).filter(group => group.id !== 'mortgage' || fundingMethod !== 'cash')
  }, [settings.data, grouped, fundingMethod])
  const selectedGroup = groups.find(group => grouped && group.id === selectedGroups[grouped]) ?? groups[0]

  useEffect(() => {
    if (!grouped || !groups.length) return
    setSelectedGroups(current => groups.some(group => group.id === current[grouped]) ? current : { ...current, [grouped]: groups[0]!.id })
  }, [groups, grouped])

  const loading = (!accessReady && !accessError) || (canReadDefaults && (settings.isPending || preferences.isPending))
  const ready = canReadDefaults && settings.data && preferences.data
  const sectionLabel = SECTIONS.find(s => s.id === section)?.label ?? 'Settings'

  return (
    <div className="set-page" aria-busy={draft.busy}>
      {loading ? <div className="set-focus"><div><Skeletons count={3} kind="row" /></div><Skeletons count={1} kind="block" /></div> : null}
      {accessError ? <Alert tight title="Settings access unavailable">{accessError}</Alert> : null}
      {settings.isError ? <Alert tight title="Defaults unavailable">{settings.error.message}</Alert> : null}
      {preferences.isError ? <Alert tight title="Preferences unavailable">{preferences.error.message}</Alert> : null}
      {savePreferences.isError ? <Alert tight title={draft.conflict(savePreferences.error) ? 'Defaults changed on another device' : 'Refurbishment was not saved'}>{savePreferences.error.message}</Alert> : null}
      {saveSettings.isError ? <Alert tight title={draft.conflict(saveSettings.error) ? 'Defaults changed on another device' : 'Defaults were not saved'}>{saveSettings.error.message}</Alert> : null}
      {draft.exactValueError && draft.dirty ? <Alert tight title="Check your settings">{draft.exactValueError}</Alert> : null}
      {draft.refurbishmentError && draft.refurbishmentDirty ? <Alert tight title="Check your refurbishment budget">{draft.refurbishmentError}</Alert> : null}
      {draft.saveMessage ? <p className="set-saved" role="status">{draft.saveMessage}</p> : null}
      {ready ? (
        <fieldset className="set-editor" disabled={draft.busy}>
          <legend className="sr-only">{sectionLabel} defaults</legend>
          {section === 'refurbishment' ? (
            <div className="set-focus">
              <nav className="set-groups" aria-label="Refurbishment setting groups">
                <button type="button" className="opt is-on" aria-pressed="true"><b>Default budget</b><span>{refurbishmentDraftSummary(draft.refurbishment)}</span></button>
              </nav>
              <div className="set-editor-col" role="region" aria-label="Default budget">
                <RefurbishmentEditor draft={draft.refurbishment} onChange={draft.updateRefurbishment} />
              </div>
            </div>
          ) : selectedGroup && grouped ? (
            <div className="set-focus">
              <nav className="set-groups" aria-label={`${sectionLabel} setting groups`}>
                {groups.map(group => (
                  <button type="button" key={group.id} className={['opt', selectedGroup.id === group.id ? 'is-on' : ''].join(' ')} aria-pressed={selectedGroup.id === group.id} onClick={() => setSelectedGroups(current => ({ ...current, [grouped]: group.id }))}>
                    <b>{group.title}</b>
                    <span>
                      {group.descriptors.slice(0, 2).map(descriptor => `${settingsFieldPresentation(descriptor).label}: ${settingsSummaryValue(descriptor, draft.assumptions[descriptor.key])}`).join(' · ')}
                      {group.descriptors.length > 2 ? ` · +${group.descriptors.length - 2} more` : ''}
                    </span>
                  </button>
                ))}
              </nav>
              <div className="set-editor-col" role="region" aria-label={selectedGroup.title}>
                <SettingsGroup key={`${grouped}-${selectedGroup.id}`} title={selectedGroup.title} description={selectedGroup.description}>
                  {selectedGroup.descriptors.map(descriptor => <SettingsField key={descriptor.key} descriptor={descriptor} value={draft.assumptions[descriptor.key]} onChange={draft.updateAssumption} invalid={draft.dirty && draft.invalidKey === descriptor.key} />)}
                </SettingsGroup>
              </div>
            </div>
          ) : <p className="t-muted">No shared defaults are available for this experience.</p>}
        </fieldset>
      ) : null}
    </div>
  )
}
